"use client";

import { useMemo, useState, type ReactNode } from "react";
import { useFormatter, useLocale, useTranslations } from "use-intl";

import { SearchIcon } from "@/components/appIcons";
import { Link } from "@/components/link";

const SEARCH_THRESHOLD = 18;
const MEMBER_PREVIEW_COUNT = 24;
type ArtistTranslations = ReturnType<typeof useTranslations>;
type MemberTimelineGroup = {
  key: string;
  label: string | null;
  members: ArtistMemberFact[];
};
type MemberPeriod = {
  label: ReactNode;
};

export interface ArtistMemberFact {
  id: string;
  name: string;
  beginDate: string | null;
  endDate: string | null;
  source: string;
}

interface ArtistMemberLedgerProps {
  members: ArtistMemberFact[];
}

/** Full membership timeline (dated + undated members, with search for large rosters). */
export function ArtistMemberLedger({ members }: ArtistMemberLedgerProps) {
  const t = useTranslations("artist");
  const tBio = useTranslations("shared.bioText");
  const locale = useLocale();
  const format = useFormatter();
  const formatNumber = (value: number) => format.number(value);

  if (members.length === 0) return null;

  return (
    <MemberLedgerValue
      members={members}
      placeholder={t("memberSearchPlaceholder")}
      showMoreLabel={tBio("showMore")}
      showLessLabel={tBio("showLess")}
      formatNumber={formatNumber}
      locale={locale}
      t={t}
    />
  );
}

function MemberLedgerValue({
  members,
  placeholder,
  showMoreLabel,
  showLessLabel,
  formatNumber,
  locale,
  t,
}: {
  members: ArtistMemberFact[];
  placeholder: string;
  showMoreLabel: string;
  showLessLabel: string;
  formatNumber: (value: number) => string;
  locale: string;
  t: ArtistTranslations;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const hasSearch = members.length > SEARCH_THRESHOLD;
  const normalizedQuery = normalizeQuery(query);
  const visibleMembers = useMemo(() => {
    if (!hasSearch || normalizedQuery.length === 0) return members;
    return members.filter((member) =>
      normalizeQuery(
        `${member.name} ${member.beginDate ?? ""} ${member.endDate ?? ""} ${member.source}`,
      ).includes(normalizedQuery),
    );
  }, [hasSearch, members, normalizedQuery]);
  const datedMembers = useMemo(
    () => visibleMembers.filter(hasMemberDate).sort(compareMembersByTimeline),
    [visibleMembers],
  );
  const undatedMembers = useMemo(
    () => visibleMembers.filter((member) => !hasMemberDate(member)).sort(compareMembersByName),
    [visibleMembers],
  );
  const shouldTruncate =
    normalizedQuery.length === 0 && visibleMembers.length > MEMBER_PREVIEW_COUNT;
  const { displayDatedMembers, displayUndatedMembers, hiddenCount } = useMemo(
    () => getDisplayMembers(datedMembers, undatedMembers, shouldTruncate && !expanded),
    [datedMembers, expanded, shouldTruncate, undatedMembers],
  );
  const groupedMembers = useMemo(
    () => groupMembersByStart(displayDatedMembers),
    [displayDatedMembers],
  );
  const hasVisibleMembers = displayDatedMembers.length > 0 || displayUndatedMembers.length > 0;

  return (
    <div className="space-y-2">
      {hasSearch && <SearchBox value={query} onChange={setQuery} placeholder={placeholder} />}
      {hasVisibleMembers ? (
        <div className="min-w-0 space-y-4">
          {displayDatedMembers.length > 0 && (
            <MemberLedgerSection
              id="artist-members-dated"
              label={t("memberDatedGroup")}
              count={datedMembers.length}
            >
              <div className="min-w-0 space-y-3">
                {groupedMembers.map((group, index) => (
                  <MemberTimelineGroup
                    key={group.key}
                    group={group}
                    isLast={index === groupedMembers.length - 1}
                    locale={locale}
                    t={t}
                  />
                ))}
              </div>
            </MemberLedgerSection>
          )}
          {displayUndatedMembers.length > 0 && (
            <MemberLedgerSection
              id="artist-members-undated"
              label={t("memberUndatedGroup")}
              count={undatedMembers.length}
            >
              <UndatedMemberList members={displayUndatedMembers} />
            </MemberLedgerSection>
          )}
        </div>
      ) : (
        <p className="py-2 text-sm font-medium text-muted-foreground">0</p>
      )}
      {shouldTruncate && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="inline-flex items-center rounded border border-border/60 bg-background/60 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
        >
          {expanded ? showLessLabel : `+${formatNumber(hiddenCount)} ${showMoreLabel}`}
        </button>
      )}
    </div>
  );
}

function MemberLedgerSection({
  id,
  label,
  count,
  children,
}: {
  id: string;
  label: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-2" aria-labelledby={id}>
      <h3 id={id} className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
        {count > 1 && <span className="ml-1 font-mono normal-case tracking-normal">({count})</span>}
      </h3>
      {children}
    </section>
  );
}

function MemberTimelineGroup({
  group,
  isLast,
  locale,
  t,
}: {
  group: MemberTimelineGroup;
  isLast: boolean;
  locale: string;
  t: ArtistTranslations;
}) {
  return (
    <div className="grid min-w-0 grid-cols-[3.5rem_minmax(0,1fr)] gap-3">
      <div className="relative flex justify-end pr-4">
        {group.label && (
          <p className="font-mono text-xs font-bold leading-5 tabular-nums text-foreground">
            {group.label}
          </p>
        )}
        <span className="absolute right-0 top-1.5 size-2 rounded-full border border-background bg-primary" />
        {!isLast && (
          <span className="absolute bottom-[-0.875rem] right-[3.5px] top-4 w-px bg-border" />
        )}
      </div>
      <ul className="relative min-w-0 space-y-1 pb-1">
        <span aria-hidden="true" className="absolute -left-3 top-2.5 h-px w-3 bg-border" />
        {group.members.map((member, index) => (
          <MemberLedgerItem
            key={`${member.id}-${member.beginDate ?? "open"}-${member.endDate ?? "open"}-${member.source}-${index}`}
            member={member}
            period={formatMemberPeriod(member.beginDate, member.endDate, t, locale)}
          />
        ))}
      </ul>
    </div>
  );
}

function MemberLedgerItem({
  member,
  period,
}: {
  member: ArtistMemberFact;
  period: MemberPeriod | null;
}) {
  return (
    <li className="flex min-w-0 flex-col gap-0.5 py-0.5 leading-5 sm:flex-row sm:items-baseline sm:gap-2">
      <Link
        to={`/artist/${member.id}`}
        className="min-w-0 max-w-full break-words text-sm font-semibold text-foreground hover:text-primary hover:underline sm:max-w-[22rem] sm:shrink-0"
      >
        {member.name}
      </Link>
      {period && (
        <>
          <span
            aria-hidden="true"
            className="hidden h-px min-w-6 flex-1 border-t border-dashed border-border/70 sm:block"
          />
          <span className="font-mono text-[11px] font-medium leading-4 text-muted-foreground sm:whitespace-nowrap">
            {period.label}
          </span>
        </>
      )}
    </li>
  );
}

function UndatedMemberList({ members }: { members: ArtistMemberFact[] }) {
  return (
    <ul className="grid min-w-0 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
      {members.map((member, index) => (
        <li key={`${member.id}-${member.source}-${index}`} className="min-w-0">
          <Link
            to={`/artist/${member.id}`}
            className="min-w-0 break-words text-sm font-semibold leading-5 text-foreground hover:text-primary hover:underline"
          >
            {member.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="relative block min-w-0 sm:max-w-xs">
      <SearchIcon
        decorative
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[15px] text-muted-foreground"
      />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-11 w-full rounded-md border border-border/70 bg-background/70 pl-7 sm:h-7 pr-2 text-xs font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/60 focus:bg-background"
      />
    </label>
  );
}

function formatMemberPeriod(
  beginDate: string | null,
  endDate: string | null,
  t: ArtistTranslations,
  locale: string,
) {
  const highlightDate = (chunks: ReactNode) => (
    <span className="font-bold text-foreground">{chunks}</span>
  );

  if (beginDate && endDate) {
    return {
      label: t.rich("memberPeriodRange", {
        start: formatPartialDate(beginDate, locale),
        end: formatPartialDate(endDate, locale),
        startDate: highlightDate,
        endDate: highlightDate,
      }),
    } satisfies MemberPeriod;
  }
  if (beginDate) {
    return {
      label: t.rich("memberPeriodFrom", {
        date: formatPartialDate(beginDate, locale),
        dateValue: highlightDate,
      }),
    } satisfies MemberPeriod;
  }
  if (endDate) {
    return {
      label: t.rich("memberPeriodUntil", {
        date: formatPartialDate(endDate, locale),
        dateValue: highlightDate,
      }),
    } satisfies MemberPeriod;
  }
  return null;
}

function groupMembersByStart(members: ArtistMemberFact[]) {
  return members.reduce<MemberTimelineGroup[]>((groups, member) => {
    const startYear = getMemberStartYear(member);
    const key = startYear ?? "unknown";
    const previousGroup = groups.at(-1);

    if (previousGroup?.key !== key) {
      return [...groups, { key, label: startYear, members: [member] }];
    }

    return [
      ...groups.slice(0, -1),
      {
        ...previousGroup,
        members: [...previousGroup.members, member],
      },
    ];
  }, []);
}

function getMemberStartYear(member: ArtistMemberFact) {
  return member.beginDate?.match(/^([0-9]{4})/)?.[1] ?? null;
}

function hasMemberDate(member: ArtistMemberFact) {
  return member.beginDate !== null || member.endDate !== null;
}

function compareMembersByTimeline(a: ArtistMemberFact, b: ArtistMemberFact) {
  const beginDateCompare = compareNullableText(a.beginDate, b.beginDate);
  if (beginDateCompare !== 0) return beginDateCompare;

  const endDateCompare = compareNullableText(a.endDate, b.endDate);
  if (endDateCompare !== 0) return endDateCompare;

  return a.name.localeCompare(b.name);
}

function compareMembersByName(a: ArtistMemberFact, b: ArtistMemberFact) {
  const nameCompare = a.name.localeCompare(b.name);
  if (nameCompare !== 0) return nameCompare;

  const sourceCompare = a.source.localeCompare(b.source);
  if (sourceCompare !== 0) return sourceCompare;

  return a.id.localeCompare(b.id);
}

function compareNullableText(a: string | null, b: string | null) {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
}

function formatPartialDate(value: string, locale: string) {
  const trimmed = value.trim();
  const match = /^([0-9]{4})(?:-([0-9]{2})(?:-([0-9]{2}))?)?$/.exec(trimmed);

  if (!match) return value;

  const [, yearText, monthText, dayText] = match;
  if (!monthText) return yearText;

  const year = Number(yearText);
  const month = Number(monthText);
  const day = dayText ? Number(dayText) : null;
  if (!isValidPartialDate(month, day)) return value;

  const date = new Date(Date.UTC(year, month - 1, day ?? 1));
  const options: Intl.DateTimeFormatOptions = day
    ? { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }
    : { month: "short", year: "numeric", timeZone: "UTC" };

  return new Intl.DateTimeFormat(locale, options).format(date);
}

function isValidPartialDate(month: number, day: number | null): boolean {
  const validMonth = month >= 1 && month <= 12;
  const validDay = day === null || (day >= 1 && day <= 31);
  return validMonth && validDay;
}

function normalizeQuery(value: string) {
  return value.trim().toLocaleLowerCase();
}

function getDisplayMembers(
  datedMembers: ArtistMemberFact[],
  undatedMembers: ArtistMemberFact[],
  truncate: boolean,
) {
  if (!truncate) {
    return {
      displayDatedMembers: datedMembers,
      displayUndatedMembers: undatedMembers,
      hiddenCount: Math.max(0, datedMembers.length + undatedMembers.length - MEMBER_PREVIEW_COUNT),
    };
  }

  if (datedMembers.length === 0) {
    const displayUndatedMembers = undatedMembers.slice(0, MEMBER_PREVIEW_COUNT);
    return {
      displayDatedMembers: [],
      displayUndatedMembers,
      hiddenCount: undatedMembers.length - displayUndatedMembers.length,
    };
  }

  if (undatedMembers.length === 0) {
    const displayDatedMembers = datedMembers.slice(0, MEMBER_PREVIEW_COUNT);
    return {
      displayDatedMembers,
      displayUndatedMembers: [],
      hiddenCount: datedMembers.length - displayDatedMembers.length,
    };
  }

  const datedBaseLimit = Math.min(datedMembers.length, Math.floor(MEMBER_PREVIEW_COUNT / 2));
  const undatedBaseLimit = Math.min(undatedMembers.length, MEMBER_PREVIEW_COUNT - datedBaseLimit);
  const unusedSlots = MEMBER_PREVIEW_COUNT - datedBaseLimit - undatedBaseLimit;
  const datedLimit = Math.min(datedMembers.length, datedBaseLimit + unusedSlots);
  const displayDatedMembers = datedMembers.slice(0, datedLimit);
  const displayUndatedMembers = undatedMembers.slice(0, MEMBER_PREVIEW_COUNT - datedLimit);

  return {
    displayDatedMembers,
    displayUndatedMembers,
    hiddenCount: datedMembers.length + undatedMembers.length - MEMBER_PREVIEW_COUNT,
  };
}
